/**
 * @fileoverview State and handlers for TaskInputQuick (omni + inline modes).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useT } from '@/i18n/useT';
import { useQuickbarPlaceholder } from '@/hooks/ui/useQuickbarPlaceholder';
import { useAuth } from '@/features/auth/useAuth';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import { mergeNewTaskCreateData } from '@/features/tasks/context/tasksContextUtils';
import { parseTaskInput } from '@/features/tasks/utils/data/taskParser';
import useAddTask from '@/features/tasks/hooks/mutations/use-task-add';

/**
 * @param {{
 *   onTaskAdd?: (payload: { title: string }) => void,
 *   onBlur?: () => void,
 *   placeholder?: string,
 *   onTaskAdded?: (taskId: string, parsed: object) => void,
 *   disableEmptyNavigate?: boolean,
 *   onInputChange?: (value: string) => void,
 *   onInputEscape?: () => void,
 * }} options
 */
export function useTaskInputQuick({
  onTaskAdd = (..._args) => {},
  onBlur = () => {},
  placeholder,
  onTaskAdded,
  disableEmptyNavigate = false,
  onInputChange,
  onInputEscape,
} = {}) {
  const [inputValue, setInputValue] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const t = useT();
  const defaultQuickbarPlaceholder = useQuickbarPlaceholder();
  const { user } = useAuth();
  const { projectFilter, contextFilter } = useTasksContext();
  const { addTask } = useAddTask();
  const navigate = useNavigate();

  const parsedPreview = useMemo(
    () => parseTaskInput(inputValue),
    [inputValue],
  );

  const handleQuickAddTask = async () => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput) return;
    if (!user) {
      console.error('Cannot add task: user not logged in.');
      return;
    }

    const parsedAttributes = parseTaskInput(trimmedInput);
    const previousValue = inputValue;
    setInputValue('');
    setErrorMessage('');

    try {
      const taskId = await addTask(
        mergeNewTaskCreateData(
          {},
          parsedAttributes,
          projectFilter,
          contextFilter,
        ),
      );
      if (taskId && onTaskAdded) {
        onTaskAdded(taskId, parsedAttributes);
      }
    } catch (error) {
      console.error('Error adding task:', error);
      setInputValue(previousValue);
      setErrorMessage(
        error.message ||
          t(
            'taskinputquick.addError',
            'An error occurred while adding the task.',
          ),
      );
    }
  };

  const handleQuickAddKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleQuickAddTask();
    }
    if (e.key === 'Escape') {
      onInputEscape?.();
      setInputValue('');
      e.target.blur();
    }
  };

  const handleQuickAddButtonClick = () => {
    if (inputValue.trim()) {
      handleQuickAddTask();
    } else if (!disableEmptyNavigate) {
      navigate('/app/today?new-task');
    }
  };

  const fieldPlaceholder = placeholder ?? defaultQuickbarPlaceholder;

  const handleInlineSubmit = () => {
    const trimmedInput = inputValue.trim();
    if (trimmedInput && onTaskAdd) {
      onTaskAdd({ title: trimmedInput });
      setInputValue('');
    } else if (onBlur) {
      onBlur();
    }
  };

  const addTaskLabel = t('taskinputquick.addTask', 'Add task');
  const createDetailedTaskLabel = t(
    'taskinputquick.createDetailedTask',
    'Create detailed task',
  );
  const trailingActionLabel = inputValue.trim()
    ? addTaskLabel
    : createDetailedTaskLabel;

  const handleInlineKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleInlineSubmit();
    } else if (e.key === 'Escape') {
      if (onBlur) onBlur();
    }
  };

  const handleInlineBlur = () => {
    if (inputValue.trim()) {
      handleInlineSubmit();
    } else if (onBlur) {
      onBlur();
    }
  };

  const handleOmniChange = (e) => {
    const next = e.target.value;
    setInputValue(next);
    setErrorMessage('');
    onInputChange?.(next);
  };

  return {
    inputValue,
    setInputValue,
    errorMessage,
    parsedPreview,
    fieldPlaceholder,
    trailingActionLabel,
    handleQuickAddKeyDown,
    handleQuickAddButtonClick,
    handleInlineSubmit,
    handleInlineKeyDown,
    handleInlineBlur,
    handleOmniChange,
  };
}
